// Contar sin señal, y que nada se pierda.
//
// EL PROBLEMA. Los movimientos ya sobrevivían a un pasillo sin cobertura
// desde que existe `cola.js`. Los conteos no: cada lectura era una llamada
// al servidor y, sin señal, no se contaba. Y contar es JUSTO lo que se
// hace en el fondo del almacén, entre estanterías metálicas, durante horas
// — el sitio y el momento en que menos señal hay de todo el turno.
//
// POR QUÉ NO SE REUSÓ `cola.js` TAL CUAL, que era lo primero que probé:
// esa cola reenvía peticiones guardadas, y reenviar «suma una» dos veces
// cuenta dos. El endpoint de captura NO es idempotente (ni el de
// recepción), así que una cola de incrementos convierte un problema de
// cobertura en un inventario mal contado — y mal contado hacia arriba, que
// nadie va a ir a buscar.
//
// LA SOLUCIÓN ES GUARDAR EL TOTAL, NO EL INCREMENTO. Aquí se anota
// «el renglón 41 va por 12», y al reconectar se manda ese 12 con el
// endpoint que FIJA la cantidad. Reenviarlo diez veces deja 12 las diez
// veces: idempotente por construcción, sin tocar el servidor.
//
// La contrapartida, y es real: si dos personas cuentan el mismo renglón a
// la vez y una está sin señal, gana la última que sincroniza en vez de
// sumarse las dos. Es el precio de poder contar sin cobertura, y es el
// comportamiento correcto para un conteo —dos personas contando el mismo
// hueco es un error de organización, y sumar sus cuentas lo escondería—.
const DB = 'tego-conteos';
const STORE = 'renglones';

function abrir() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE)) {
        r.result.createObjectStore(STORE, { keyPath: 'clave' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

function conStore(modo, fn) {
  return abrir().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, modo);
    const rq = fn(t.objectStore(STORE));
    t.oncomplete = () => res(rq && rq.result);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  }));
}

const clave = (idSesion, idRenglon) => `${idSesion}:${idRenglon}`;

export const pendientesConteo = () => conStore('readonly', (s) => s.getAll());

export async function contarPendientesConteo() {
  return (await pendientesConteo()).length;
}

/** Lo anotado localmente para una hoja: {id_renglon: cantidad}. */
export async function anotadosDe(idSesion) {
  const todos = await pendientesConteo();
  return Object.fromEntries(
    todos.filter((x) => x.id_sesion === idSesion)
      .map((x) => [x.id_renglon, x.cantidad]),
  );
}

/**
 * Anota el TOTAL de un renglón. Devuelve el total resultante.
 *
 * Se guarda SIEMPRE antes de intentar enviarlo: la durabilidad primero es
 * la misma regla que en la cola de movimientos, y aquí importa igual —
 * quien cuenta no puede perder media hora de trabajo porque el teléfono
 * eligió mal el momento de quedarse sin señal.
 */
export async function anotarTotal(idSesion, idRenglon, cantidad) {
  await conStore('readwrite', (s) => s.put({
    clave: clave(idSesion, idRenglon),
    id_sesion: idSesion,
    id_renglon: idRenglon,
    cantidad,
    _ts: Date.now(),
  }));
  return cantidad;
}

/** Suma `unidades` al total anotado (o al que ya tuviera el servidor). */
export async function sumarLocal(idSesion, idRenglon, unidades, baseServidor) {
  const previos = await anotadosDe(idSesion);
  const base = previos[idRenglon] ?? Number(baseServidor ?? 0);
  return anotarTotal(idSesion, idRenglon, base + unidades);
}

export const olvidar = (idSesion, idRenglon) =>
  conStore('readwrite', (s) => s.delete(clave(idSesion, idRenglon)));

/**
 * Manda lo anotado. Devuelve {enviados, restantes}.
 *
 * `api` se pasa por parámetro para que este módulo no dependa del cliente
 * HTTP: así se puede probar sin red.
 */
export async function sincronizarConteos(api) {
  if (!navigator.onLine) return { enviados: 0, restantes: await contarPendientesConteo() };

  let enviados = 0;
  for (const r of await pendientesConteo()) {
    try {
      // El endpoint que FIJA la cantidad, no el que suma: es lo que hace
      // que reenviar no cuente de más.
      await api.post(`/conteos/${r.id_sesion}/renglones/${r.id_renglon}`,
                     { cantidad: r.cantidad });
      await olvidar(r.id_sesion, r.id_renglon);
      enviados += 1;
    } catch (e) {
      // Una hoja ya cerrada, o un renglón que ya no existe, no se arregla
      // reintentando: se suelta para que la cola no se atasque para
      // siempre por un renglón muerto. El resto sigue.
      if (e?.status && [400, 404, 409, 422].includes(e.status)) {
        await olvidar(r.id_sesion, r.id_renglon);
      }
    }
  }
  return { enviados, restantes: await contarPendientesConteo() };
}
